import React from 'react';
import { FaCopy, FaPlus } from 'react-icons/fa';
import ClipCard from './ClipCard';
import { cutHintForClip, timelineCutsForTag } from './editHintsUtils';

/**
 * Ordered clip list + new-clip form. Scrolls independently from the video pane.
 */
export default function VideoClipsPanel({
  clips = [],
  previewingClipIndex = null,
  activeField = null,
  onActivateField,
  onSaveField,
  onCancelField,
  onUpdateClipTime,
  onDeleteClip,
  onEditCrop,
  onRemoveCrop,
  onSeek,
  onPreviewClip,
  onNudgeClipTime,
  onMuteClip,
  onEffectChange,
  showMute = false,
  showEffect = false,
  showTag = false,
  clipTagToggles = {},
  onClipTagToggle,
  onClipTagChange,
  manualInput = '',
  onManualInputChange,
  isSavingStart = true,
  onManualSubmit,
  onCopyClips,
  onUseAll,
  editHints = null,
  emptyHint = 'No clips yet — scrub the video, then use Set start / Set end under the player.',
}) {
  return (
    <div className="video-clips-panel">
      <div className="video-clips-panel__head">
        <div className="video-clips-panel__head-main">
          <h3 className="video-clips-panel__title">Clips</h3>
          <span className="video-clips-panel__count">{clips.length} total</span>
        </div>
        {onUseAll && (
          <button type="button" className="clip-use-all-btn" onClick={onUseAll}>
            Use all
          </button>
        )}
      </div>

      {clips.length > 0 && (
        <p className="video-clips-panel__verify-hint">
          Preview each clip on the source. Tagged clips with a cut hint show <strong>Hint</strong> — click to expand.
        </p>
      )}

      <div className="video-clips-panel__list">
        {clips.length === 0 ? (
          <p className="video-clips-panel__empty">{emptyHint}</p>
        ) : (
          clips.map((clip, clipIdx) => (
            <ClipCard
              key={clipIdx}
              clip={clip}
              clipIndex={clipIdx}
              cutHint={cutHintForClip(editHints, clip)}
              timelineCuts={timelineCutsForTag(editHints, clip.tag)}
              activeField={activeField}
              onActivateField={onActivateField}
              onSaveField={onSaveField}
              onCancelField={onCancelField}
              onSeek={onSeek}
              onDelete={onDeleteClip}
              onEditCrop={onEditCrop}
              onRemoveCrop={onRemoveCrop}
              onPreview={onPreviewClip}
              onNudgeTime={onNudgeClipTime}
              isPreviewing={previewingClipIndex === clipIdx}
              onMuteToggle={onMuteClip}
              onEffectChange={onEffectChange}
              showMute={showMute}
              showEffect={showEffect}
              showTag={showTag}
              tagEnabled={!!clipTagToggles[clipIdx]}
              onTagToggle={onClipTagToggle}
              tagValue={clip.tag || ''}
              onTagChange={onClipTagChange}
            />
          ))
        )}
      </div>

      <div className="video-clips-panel__new manual-clip-inputs">
        <div className="video-clips-panel__new-head">
          <FaPlus aria-hidden="true" />
          <span>Add new clip from video</span>
        </div>
        <form onSubmit={onManualSubmit}>
          <input
            type="text"
            value={manualInput}
            onChange={onManualInputChange}
            placeholder="HH:MM:SS.ms"
            spellCheck={false}
          />
          <button
            type="submit"
            className={isSavingStart ? 'clip-new-btn--start' : 'clip-new-btn--end'}
          >
            {isSavingStart ? '▶ Save start' : '⏹ Save end'}
          </button>
        </form>
        <p className="video-clips-panel__hint video-clips-panel__hint--desktop-only">
          Desktop: scrub the video and press Enter for start, then end. Use Preview on each clip to
          check the range. ±5s buttons step the playhead without changing saved timestamps.
        </p>
        <p className="video-clips-panel__hint video-clips-panel__hint--mobile-only">
          Use the video controls to scrub, then Set start / Set end below the player. Preview plays
          start → end on the source.
        </p>
      </div>

      {onCopyClips && clips.length > 0 && (
        <button type="button" className="copy-clip-btn" onClick={onCopyClips}>
          <FaCopy aria-hidden="true" /> Copy clip data
        </button>
      )}
    </div>
  );
}
